import React, { type Ref, type SyntheticEvent, useEffect } from 'react';

import { useIntl } from 'react-intl';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { useAnalyticsEvents as useAllAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import AddIcon from '@atlaskit/icon/core/add';
import TeamsIcon from '@atlaskit/icon/core/teams';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics/use-analytics-events';
import { token } from '@atlaskit/tokens';

import messages from './messages';

type TeamButtonTriggerProps = {
	triggerProps: TriggerProps | { ref: Ref<any> };
	numberOfTeams: number;
	onClick: (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => void;
	useButtonAriaLabel?: boolean;
};

const styles = cssMap({
	baseButton_motion: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		display: 'flex',
		alignItems: 'center',
		height: '32px',
		width: '32px',
		justifyContent: 'center',
		color: token('color.text.subtle'),
		transition: token('motion.button.hovered'),
		'&:hover': { backgroundColor: token('color.background.neutral.subtle.hovered') },
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
	baseButtonWithNumber_motion: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		display: 'flex',
		alignItems: 'center',
		height: '32px',
		width: '56px',
		justifyContent: 'center',
		color: token('color.text.subtle'),
		transition: token('motion.button.hovered'),
		'&:hover': { backgroundColor: token('color.background.neutral.subtle.hovered') },
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
});

export const TeamButtonTrigger = ({
	triggerProps,
	numberOfTeams,
	onClick,
	useButtonAriaLabel = false,
}: TeamButtonTriggerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const { fireEvent } = useAnalyticsEvents();
	const { createAnalyticsEvent } = useAllAnalyticsEvents();

	useEffect(() => {
		const result = createAnalyticsEvent({})?.context.find(
			(item) => item.attributes && item.attributes.scope !== undefined,
		);

		fireEvent('ui.teamButton.viewed', {
			teamsCount: numberOfTeams,
			scope: result?.attributes?.scope ?? '',
			version: 'm2',
		});
	}, [createAnalyticsEvent, fireEvent, numberOfTeams]);

	const { 'aria-expanded': _removed, ...restTriggerProps } = triggerProps as TriggerProps;

	return (
		<Pressable
			{...restTriggerProps}
			onClick={onClick}
			xcss={numberOfTeams !== 1 ? styles.baseButtonWithNumber_motion : styles.baseButton_motion}
			testId="team-button-trigger"
			aria-label={useButtonAriaLabel ? formatMessage(messages.teamBtnLabel) : undefined}
			{...(fg('jfp_a11y_team_attributes_defined_inappropriately')
				? {
						'aria-haspopup': 'dialog',
					}
				: { 'aria-haspopup': 'true' })}
		>
			<TeamsIcon
				color="currentColor"
				label={useButtonAriaLabel ? '' : formatMessage(messages.iconLabel)}
			/>
			{numberOfTeams > 1 && (
				<Box as="span" paddingInlineStart="space.100">
					{numberOfTeams}
				</Box>
			)}
			{numberOfTeams === 0 && (
				<Box as="span" paddingInlineStart="space.050">
					<AddIcon color="currentColor" label={formatMessage(messages.addTeamIconLabel)} />
				</Box>
			)}
		</Pressable>
	);
};
